import { secureStorage } from '../crypto/secureStorage'
import { syncEngine } from './syncEngine'

export type SyncCheckpointStatus = 'ok' | 'error'

const SYNC_PROTOCOL_VERSION = 1
const CHECKPOINT_THROTTLE_MS = 15 * 60 * 1000
const CHECKPOINT_CACHE_KEY = 'superelectric_ink_sync_checkpoint_v1'

let inFlight: Promise<boolean> | null = null
let inFlightStatus: SyncCheckpointStatus | null = null

interface CachedCheckpoint {
  deviceId: string
  status: SyncCheckpointStatus
  reportedAt: number
}

function readCachedCheckpoint(): CachedCheckpoint | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(CHECKPOINT_CACHE_KEY) || 'null')
    return parsed && typeof parsed.deviceId === 'string' &&
      (parsed.status === 'ok' || parsed.status === 'error') &&
      typeof parsed.reportedAt === 'number'
      ? parsed as CachedCheckpoint
      : null
  } catch {
    return null
  }
}

/**
 * The server accepts a short version token (letters, digits, . + _ -, at most
 * 32 characters). Builds are labeled "<build time> (<git hash>)", which it
 * rejects, so send the hash; anything else is reduced to the allowed form.
 */
export function checkpointAppVersion(buildId: string): string {
  const hash = /\(([0-9A-Za-z]{4,40})\)\s*$/.exec(buildId)?.[1]
  const cleaned = (hash ?? buildId).replace(/[^0-9A-Za-z.+_-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 32)
  return cleaned || 'unknown'
}

function cacheCheckpoint(value: CachedCheckpoint): void {
  try {
    localStorage.setItem(CHECKPOINT_CACHE_KEY, JSON.stringify(value))
  } catch {}
}

/**
 * Stores one last-known sync result for this activated device. Reports are
 * best-effort and deliberately throttled: a status transition is immediate,
 * while repeated successful syncs overwrite the row at most every 15 minutes.
 */
export async function reportSyncCheckpoint(
  accessToken: string | null,
  status: SyncCheckpointStatus,
  options: { force?: boolean } = {},
): Promise<boolean> {
  if (!accessToken || typeof window === 'undefined' || !navigator.onLine) return false
  if (inFlight) {
    if (inFlightStatus === status) return inFlight
    await inFlight
    return reportSyncCheckpoint(accessToken, status, options)
  }

  const deviceId = await secureStorage.getActivationDeviceId()
  if (!deviceId) return false

  const previous = readCachedCheckpoint()
  if (
    !options.force &&
    previous?.deviceId === deviceId &&
    previous.status === status &&
    Date.now() - previous.reportedAt < CHECKPOINT_THROTTLE_MS
  ) {
    return false
  }

  inFlightStatus = status
  inFlight = (async () => {
    try {
      const response = await fetch(
        `${syncEngine.getApiUrl()}/api/devices/${encodeURIComponent(deviceId)}/checkpoint`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
          body: JSON.stringify({
            status,
            appVersion: checkpointAppVersion(typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'unknown'),
            protocolVersion: SYNC_PROTOCOL_VERSION,
          }),
        },
      )
      if (!response.ok) return false
      cacheCheckpoint({ deviceId, status, reportedAt: Date.now() })
      return true
    } catch {
      return false
    }
  })().finally(() => {
    inFlight = null
    inFlightStatus = null
  })

  return inFlight
}
